import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../../services/api'; // Centralized API service
import Footer from '../layout/Footer';
import Navbar from '../layout/Navbar';

// =================================================================
// == POST CARD COMPONENT
// =================================================================
const PostCard = ({ post }) => (
    <article className="bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-[#2A2A2A] rounded-lg hover:ring-2 hover:ring-sky-200 lg:p-6 md:p-6 p-4 duration-300">
        <Link to={`/blog/${post.slug}`}>
            {(post.thumbnail || post.featured_image) && <img src={post.thumbnail || post.featured_image} alt={post.title} className="w-full h-72 object-cover rounded-lg" />}
        </Link>
        {post.category && <p className="text-xs text-black dark:text-[#A3A3A3] my-4">{post.category.name.toUpperCase()}</p>}
        <h2 className="text-xl font-semibold text-black dark:text-white">
            <Link to={`/blog/${post.slug}`} className="hover:text-sky-600 dark:hover:text-[#7DD3FC] transition-colors duration-300">{post.title}</Link>
        </h2>
        <p className="text-xs text-gray-600 dark:text-[#A3A3A3] mt-2">
            {new Date(post.created_on).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
        <div className="flex items-center mt-4">
            <img src={post.author.avatar} alt={`${post.author.first_name} ${post.author.last_name}`} className="w-10 h-10 rounded-full mr-2 shadow bg-gray-200 dark:bg-gray-700 object-cover" />
            <div>
                <p className="text-sm text-black dark:text-[#A3A3A3]">{post.author.first_name} {post.author.last_name}</p>
            </div>
        </div>
    </article>
);


// =================================================================
// == BLOG PAGE COMPONENT
// =================================================================
const BlogPage = () => {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Promise.all to fetch both simultaneously
                const [postsResponse, categoriesResponse] = await Promise.all([
                    API.get('blog/posts/'),
                    API.get('blog/categories/')
                ]);
                setPosts(postsResponse.data);
                setCategories(categoriesResponse.data);
            } catch (error) {
                console.error("Failed to fetch posts:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredPosts = selectedCategory === 'all'
        ? posts
        : posts.filter(post => post.category?.name === selectedCategory);

    return (
        <div className="bg-white dark:bg-[#141414] text-black dark:text-[#D9D9D9] min-h-screen flex flex-col">

            {/* =================================================================
            == META DATA SECTION
            ================================================================= */}
            <title>Blog | Trueway</title>
            <meta name="description" content="Updates and notes from the Trueway team." />
            {/* Open Graph and other meta tags here directly */}

            <Navbar />

            {/* =================================================================
            == MAIN CONTENT SECTION
            ================================================================= */}
            <main className="flex-grow py-20 px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="flex justify-between items-center mb-8">
                        <div className="flex flex-col items-start flex-1">
                            <h1 className="text-2xl font-semibold text-start text-black dark:text-gray-100">Blog</h1>
                            <p className="text-sm text-start text-sky-700 dark:text-[#D9D9D9] mt-1">Updates and notes from the Trueway team</p>
                        </div>

                        {/* =================================================================
                        == CATEGORY FILTER SECTION
                        ================================================================= */}
                        <div className="relative">
                            <select
                                id="sortBy"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="bg-white dark:bg-[#1F1F1F] rounded-lg text-sm text-gray-900 dark:text-[#D9D9D9] px-3 py-2 pr-8 cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-200 dark:focus:ring-[#7DD3FC] hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-all duration-200 appearance-none"
                            >
                                <option value="all">All Categories</option>
                                {categories.map(category => (
                                    <option key={category.name} value={category.name}>{category.name}</option>
                                ))}
                            </select>
                            <svg className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-[#A3A3A3] pointer-events-none" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                        </div>
                    </div>

                    {/* =================================================================
                    == POSTS GRID SECTION
                    ================================================================= */}
                    {loading ? (
                        <p>Loading posts...</p>
                    ) : filteredPosts.length > 0 ? ( // filteredPosts
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {filteredPosts.map(post => ( // filteredPosts
                                <PostCard key={post.id} post={post} />
                            ))}
                        </div>
                    ) : (
                        <div className="max-w-2xl mx-auto text-center py-12">
                            <div className="flex flex-col items-center space-y-4">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-9 h-9">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z" />
                                </svg>
                                <p className="text-base text-black dark:text-[#A3A3A3]">No posts yet. Check back soon for updates from the Trueway team!</p>
                            </div>
                        </div>
                    )}
                    {/* Pagination can be added here later */}
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default BlogPage;